// The hardware-first quiz's six questions. This is the fi-quizz step set
// (fi-quizz/src/quiz/steps.ts) minus `species` and `petName`: the sheet only
// ever asks about a dog, so every answer id here matches fi-quizz's so the
// fi_quiz cookie, the Customer.io attributes and the rules table stay one
// vocabulary across both quizzes.

export type AgeId = 'under_1' | '1_3' | '4_7' | '8_plus';
export type SizeId = 'extra_small' | 'small' | 'medium' | 'large';
export type ResidenceId = 'city' | 'suburbs' | 'great_outdoors';
export type ExerciseId =
  | 'leashed_walks'
  | 'night_walks'
  | 'hiking'
  | 'roams_free'
  | 'dog_parks'
  | 'swimming'
  | 'roams_yard'
  | 'running';
export type ImportanceId =
  | 'battery'
  | 'night_light'
  | 'durability'
  | 'style'
  | 'size'
  | 'attach'
  | 'waterproof'
  | 'lightweight';

export interface QuizOption<T extends string> {
  id: T;
  labelKey: string;
  /** Second line under the label. Radio steps only; the chip steps are single-line. */
  sublabelKey?: string;
}

export type StepId = 'age' | 'size' | 'residence' | 'exercise' | 'importance' | 'email';

interface BaseStep {
  id: StepId;
  titleKey: string;
}

/** Single-select. Advances on tap, so these steps carry no Next button. */
export interface RadioStep<T extends string> extends BaseStep {
  kind: 'radio';
  options: readonly QuizOption<T>[];
}

/** Multi-select, capped. Picks past the cap are refused, not rotated. */
export interface ChipStep<T extends string> extends BaseStep {
  kind: 'chip';
  subtitleKey: string;
  maxSelect: number;
  options: readonly QuizOption<T>[];
}

export interface EmailStep extends BaseStep {
  kind: 'email';
}

export type QuizStep =
  | RadioStep<AgeId>
  | RadioStep<SizeId>
  | RadioStep<ResidenceId>
  | ChipStep<ExerciseId>
  | ChipStep<ImportanceId>
  | EmailStep;

const MAX_MULTI_SELECT = 3;

export const STEPS: readonly QuizStep[] = [
  {
    id: 'age',
    kind: 'radio',
    titleKey: 'hf_quiz_age_title',
    options: [
      { id: 'under_1', labelKey: 'hf_quiz_age_under_1', sublabelKey: 'hf_quiz_age_under_1_sub' },
      { id: '1_3', labelKey: 'hf_quiz_age_1_3', sublabelKey: 'hf_quiz_age_1_3_sub' },
      { id: '4_7', labelKey: 'hf_quiz_age_4_7', sublabelKey: 'hf_quiz_age_4_7_sub' },
      { id: '8_plus', labelKey: 'hf_quiz_age_8_plus', sublabelKey: 'hf_quiz_age_8_plus_sub' },
    ],
  },
  {
    id: 'size',
    kind: 'radio',
    titleKey: 'hf_quiz_size_title',
    options: [
      { id: 'extra_small', labelKey: 'hf_quiz_size_extra_small', sublabelKey: 'hf_quiz_size_extra_small_sub' },
      { id: 'small', labelKey: 'hf_quiz_size_small', sublabelKey: 'hf_quiz_size_small_sub' },
      { id: 'medium', labelKey: 'hf_quiz_size_medium', sublabelKey: 'hf_quiz_size_medium_sub' },
      { id: 'large', labelKey: 'hf_quiz_size_large', sublabelKey: 'hf_quiz_size_large_sub' },
    ],
  },
  {
    id: 'residence',
    kind: 'radio',
    titleKey: 'hf_quiz_residence_title',
    options: [
      { id: 'city', labelKey: 'hf_quiz_residence_city' },
      { id: 'suburbs', labelKey: 'hf_quiz_residence_suburbs' },
      // fi-quizz names rural `great_outdoors`; keep its id so the payloads match.
      { id: 'great_outdoors', labelKey: 'hf_quiz_residence_rural' },
    ],
  },
  {
    id: 'exercise',
    kind: 'chip',
    titleKey: 'hf_quiz_exercise_title',
    subtitleKey: 'hf_quiz_select_up_to_3',
    maxSelect: MAX_MULTI_SELECT,
    options: [
      { id: 'leashed_walks', labelKey: 'hf_quiz_exercise_leashed_walks' },
      { id: 'night_walks', labelKey: 'hf_quiz_exercise_night_walks' },
      { id: 'hiking', labelKey: 'hf_quiz_exercise_hiking' },
      { id: 'roams_free', labelKey: 'hf_quiz_exercise_roams_free' },
      { id: 'dog_parks', labelKey: 'hf_quiz_exercise_dog_parks' },
      { id: 'swimming', labelKey: 'hf_quiz_exercise_swimming' },
      { id: 'roams_yard', labelKey: 'hf_quiz_exercise_roams_yard' },
      { id: 'running', labelKey: 'hf_quiz_exercise_running' },
    ],
  },
  {
    id: 'importance',
    kind: 'chip',
    titleKey: 'hf_quiz_importance_title',
    subtitleKey: 'hf_quiz_select_up_to_3',
    maxSelect: MAX_MULTI_SELECT,
    options: [
      { id: 'battery', labelKey: 'hf_quiz_importance_battery' },
      { id: 'night_light', labelKey: 'hf_quiz_importance_night_light' },
      { id: 'durability', labelKey: 'hf_quiz_importance_durability' },
      { id: 'style', labelKey: 'hf_quiz_importance_style' },
      { id: 'size', labelKey: 'hf_quiz_importance_size' },
      { id: 'attach', labelKey: 'hf_quiz_importance_attach' },
      { id: 'waterproof', labelKey: 'hf_quiz_importance_waterproof' },
      { id: 'lightweight', labelKey: 'hf_quiz_importance_lightweight' },
    ],
  },
  { id: 'email', kind: 'email', titleKey: 'hf_quiz_email_title' },
];

/** Segments in the progress bar. The calculating and result screens are not counted. */
export const PROGRESS_STEP_COUNT = STEPS.length;

export interface QuizAnswers {
  /**
   * Always `dog`: every question in this flow says "your dog". Stamped so the
   * payload and the rules table keep fi-quizz's species fact, whose only use is
   * the cats-mini-only gate.
   */
  species: 'dog';
  age: AgeId | null;
  size: SizeId | null;
  residence: ResidenceId | null;
  exercise: ExerciseId[];
  importance: ImportanceId[];
  email: string | null;
}

export const EMPTY_ANSWERS: QuizAnswers = {
  species: 'dog',
  age: null,
  size: null,
  residence: null,
  exercise: [],
  importance: [],
  email: null,
};
